import { isAgentFamily } from '../store/graphFamily';
import { create } from 'zustand';
import { forgetAllNavigation, forgetNavigation, recallNavigation, rememberNavigation } from './navigationHistory';
import { readNavigationPayload, sameNavigationOrigin, type NavigationOrigin, type NavigationPayload, validCoordinate } from './navigation';

export type NavigationPreview = NavigationPayload;
/** `turn`: the chat turn whose run produced the route — the chat draws its card
 *  under that turn's answer (`chat/Thread.tsx`), not in a side column.
 *  `thread`: the conversation the held frame belongs to — what its stored copy is keyed by
 *  (`navigationHistory.ts`). `restored`/`anchorId`: the frame came back from that copy after a reload (B13): a
 *  turn number means nothing in a new session, so it sits under the bubble `anchorId` and `turn` is `null`. */
type NavigationState = {
  preview: NavigationPreview | null; turn: number | null;
  thread: string | null; anchorId: string | null; restored: boolean; restoredAt: number;
  /** The place changed: the live card is no longer for it (and neither is its stored copy). A card restored from
   *  history stays through `RESTORE_GRACE_MS` only — a GPS fix landing right after the reload is the first place of
   *  the session, not a change that made the card wrong; a place the visitor sets later is (review of !169). */
  reset: () => void;
  /** A new chat: whatever card there is goes, restored or not, with the stored copy of `threadId` (the open one by default). */
  wipe: (threadId?: string) => void;
  /** «Забыть меня»: the card and every thread's stored copy. */
  wipeAll: () => void;
  /** Put the card of `threadId` back under its answer after a reload; `false` when there is none, its answer is not
   *  on screen (`hasBubble`), or a LIVE frame is already there (fresher — never overridden). */
  restore: (threadId: string, hasBubble: (id: string) => boolean) => boolean;
  /** The turn is over: keep its card for a reload. Only the card this very turn's run produced. */
  remember: (turn: number, anchorId: string | null, threadId: string) => void;
};
const EMPTY = { preview: null, turn: null, thread: null, anchorId: null, restored: false, restoredAt: 0 } as const;
/** How long after a restore a change of place is the session's first fix, not the visitor's move. */
export const RESTORE_GRACE_MS = 15_000;
export const useNavigation = create<NavigationState>((set, get) => ({
  ...EMPTY,
  reset: () => {
    const state = get();
    if (state.restored && Date.now() - state.restoredAt < RESTORE_GRACE_MS) return;
    forgetNavigation(state.thread);
    set({ ...EMPTY });
  },
  wipe: (threadId) => {
    forgetNavigation(threadId ?? get().thread);
    set({ ...EMPTY });
  },
  wipeAll: () => {
    forgetAllNavigation();
    set({ ...EMPTY });
  },
  restore: (threadId, hasBubble) => {
    if (get().preview) return false;
    const kept = recallNavigation(threadId);
    if (!kept || !hasBubble(kept.anchorId)) return false;
    set({ preview: kept.payload, turn: null, thread: threadId, anchorId: kept.anchorId, restored: true, restoredAt: Date.now() });
    return true;
  },
  remember: (turn, anchorId, threadId) => {
    const state = get();
    if (!state.preview || state.restored || state.turn !== turn || state.thread !== threadId || !anchorId) return;
    rememberNavigation(threadId, anchorId, state.preview);
  },
}));

export type NavigationGate = {
  assistantId: string;
  threadId: string;
  requestThreadId: string;
  currentOrigin: NavigationOrigin | null;
  requestOrigin: NavigationOrigin | null;
  currentLocationAt: number | null;
  requestLocationAt: number | null;
  currentTurn: number;
  requestTurn: number;
  currentRevision?: number;
  requestRevision?: number;
  currentResetAt?: number | null;
  requestResetAt?: number | null;
  currentSelectionId?: string;
  adoptedSelection?: { origin: NavigationOrigin; at: number; id: string };
};

function currentRequest(gate: NavigationGate, agentNav: boolean): boolean {
  return agentNav && isAgentFamily(gate.assistantId) && gate.threadId === gate.requestThreadId &&
    gate.currentTurn === gate.requestTurn && gate.currentRevision === gate.requestRevision &&
    gate.currentResetAt === gate.requestResetAt;
}
function samePosition(a: NavigationOrigin | null | undefined, b: NavigationOrigin | null | undefined): boolean {
  return a === null && b === null || sameNavigationOrigin(a, b);
}

export type ChatLocationSelection = {
  source: 'chat'; point: { lat: number; lon: number }; label: string; at: number; selection_id: string;
  request_origin: NavigationOrigin | null; request_location_at: number | null; request_reset_at: number | null;
};

/** A declared place arrives only in custom SSE, never restored from history (the route card under the answer is —
 *  from the browser's own copy, `navigationHistory.ts`; a declared place is not, it is the visitor's and is declared again). */
export function readLocationSelection(raw: unknown, gate: NavigationGate, agentNav = true): ChatLocationSelection | null {
  if (!currentRequest(gate, agentNav) || !raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  const point = value.point as { lat: unknown; lon: unknown } | null;
  const snapshot = value.request_origin as NavigationOrigin | null;
  if (value.source !== 'chat' || !point || !validCoordinate(point.lat, point.lon) ||
      typeof value.label !== 'string' || !value.label.trim() || value.label.length > 300 ||
      typeof value.at !== 'number' || !Number.isFinite(value.at) || value.at <= 0 ||
      typeof value.selection_id !== 'string' || !value.selection_id.trim() || value.selection_id.length > 120 ||
      !(value.request_reset_at === null || typeof value.request_reset_at === 'number' && Number.isFinite(value.request_reset_at)) ||
      value.request_reset_at !== (gate.requestResetAt ?? null) ||
      !(value.request_location_at === null || typeof value.request_location_at === 'number' && Number.isFinite(value.request_location_at)) ||
      !(snapshot === null || snapshot && validCoordinate(snapshot.lat, snapshot.lon) && typeof snapshot.source === 'string') ||
      !samePosition(snapshot, gate.requestOrigin) || value.request_location_at !== gate.requestLocationAt) return null;
  const effective = gate.adoptedSelection;
  if (!samePosition(gate.currentOrigin, effective?.origin ?? gate.requestOrigin) ||
      gate.currentLocationAt !== (effective?.at ?? gate.requestLocationAt)) return null;
  return { source: 'chat', point: { lat: point.lat as number, lon: point.lon as number }, label: value.label,
    at: value.at, selection_id: value.selection_id, request_origin: snapshot, request_location_at: value.request_location_at as number | null, request_reset_at: value.request_reset_at as number | null };
}

/** Accept only a current Agent route for the position used by this run. */
export function acceptNavigation(raw: unknown, gate: NavigationGate, agentNav = true): boolean {
  if (!currentRequest(gate, agentNav)) return false;
  const payload = readNavigationPayload(raw);
  if (!payload || payload.location_at !== gate.currentLocationAt) return false;
  // A walk from a museum object the visitor NAMED as its start (owner 10.10.2026): the start is not
  // their place — the run's own place rides beside it as `request_origin`, and that is what is matched.
  const namedStart = payload.scope !== 'arrival' && payload.origin?.source === 'start';
  if (gate.adoptedSelection) {
    const selection = gate.adoptedSelection;
    const explicitArrivalAddress = payload.scope === 'arrival' && payload.origin?.source === 'address';
    if (payload.origin_selection_id !== selection.id || gate.currentSelectionId !== selection.id ||
        payload.location_at !== selection.at || !explicitArrivalAddress && !namedStart && !sameNavigationOrigin(payload.origin, selection.origin) ||
        !sameNavigationOrigin(gate.currentOrigin, selection.origin) ||
        !samePosition(payload.request_origin, gate.requestOrigin) || payload.request_location_at !== gate.requestLocationAt || payload.request_reset_at !== (gate.requestResetAt ?? null)) return false;
  } else {
    if (payload.location_at !== gate.requestLocationAt) return false;
    if (gate.currentOrigin?.source === 'chat' && (!gate.currentSelectionId || payload.origin_selection_id !== gate.currentSelectionId ||
        !samePosition(payload.request_origin, gate.requestOrigin) || payload.request_location_at !== gate.requestLocationAt ||
        payload.request_reset_at !== (gate.requestResetAt ?? null))) return false;
    if (payload.request_reset_at !== undefined && payload.request_reset_at !== (gate.requestResetAt ?? null)) return false;
    if (payload.request_location_at !== undefined && payload.request_location_at !== gate.requestLocationAt) return false;
    if (payload.scope === 'arrival') {
      if (!samePosition(payload.request_origin, gate.currentOrigin) || !samePosition(payload.request_origin, gate.requestOrigin)) return false;
      // Only address resolution may replace the explicit panel origin.
      if (payload.origin && payload.origin.source !== 'address' && !sameNavigationOrigin(payload.origin, payload.request_origin)) return false;
    } else if (namedStart) {
      if (!samePosition(payload.request_origin, gate.currentOrigin) || !samePosition(payload.request_origin, gate.requestOrigin)) return false;
    } else if (!samePosition(payload.origin, gate.currentOrigin) || !samePosition(payload.origin, gate.requestOrigin)) return false;
  }
  // the card this frame replaces is stale from now on — its copy must not come back after a reload instead
  forgetNavigation(gate.requestThreadId);
  useNavigation.setState({ preview: payload, turn: gate.requestTurn, thread: gate.requestThreadId, anchorId: null, restored: false, restoredAt: 0 });
  return true;
}
